import { useCallback, useRef } from 'react';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors, useAorDescendants } from 'hooks/useAors';
import { getExtentFromGeoJson } from '../../LayersTab/utils/getExtentFromGeoJson';
import type { ArcgisMapView } from '../../LayersTab/utils/arcgisView';
import { openAorPopup } from './openAorPopup';
import { findAorByHomeId } from './findAorByHomeId';
import { AreaOfResponsibility } from '../../../../../types/aor';

type ZoomToAorByIdOptions = {
  filterIds?: string[];
  ensureVisible?: boolean;
};

export const isGoToInterruptedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const maybeName = 'name' in error ? error.name : undefined;
  const maybeMessage = 'message' in error ? error.message : undefined;

  return (
    maybeName === 'view:goto-interrupted' ||
    maybeMessage === 'Goto was interrupted'
  );
};

/**
 * Pure function to zoom to a specific AOR by ID. Does not depend on React hooks
 * or context.
 */
export async function performZoomToAor(
  aorId: string,
  mapView: ArcgisMapView | null,
  aors: AreaOfResponsibility[] | undefined,
  options: ZoomToAorByIdOptions & {
    currentAorFilterIds?: string[] | null;
    isAorVisible?: boolean;
    onFilterChange?: (filterIds: string[]) => void;
    onVisibilityChange?: (visible: boolean) => void;
    isZoomingRef?: React.MutableRefObject<boolean>;
  }
) {
  const isZoomingRef = options.isZoomingRef;
  if (isZoomingRef?.current || !mapView || !aors) return;
  if (isZoomingRef) isZoomingRef.current = true;

  try {
    const targetAor = aors.find((a) => a.id === aorId);
    if (!targetAor) return;

    const aorGeoJson = targetAor.geolocation?.geojson?.data;
    if (!aorGeoJson) return;

    const extent = getExtentFromGeoJson(aorGeoJson);
    if (!extent) return;

    const nextFilterIds = options.filterIds;
    const hasSameFilter =
      !!nextFilterIds &&
      !!options.currentAorFilterIds &&
      options.currentAorFilterIds.length === nextFilterIds.length &&
      nextFilterIds.every((id) => options.currentAorFilterIds?.includes(id));

    // Ensure visibility/filter first so popup opens against the stable, current layer.
    if (options.ensureVisible && !options.isAorVisible) {
      options.onVisibilityChange?.(true);
    }
    if (nextFilterIds && !hasSameFilter) {
      options.onFilterChange?.(nextFilterIds);
    }

    try {
      await mapView.goTo(extent);
    } catch (error) {
      if (!isGoToInterruptedError(error)) {
        throw error;
      }
      return;
    }
    await openAorPopup(mapView, targetAor.id);
  } finally {
    if (isZoomingRef) isZoomingRef.current = false;
  }
}

/**
 * Pure function to zoom to the user's home AOR. Does not depend on React hooks
 * or context. Descendant IDs are fetched from the backend by the caller.
 */
export async function performZoomToHomeAor(
  mapView: ArcgisMapView | null,
  aors: AreaOfResponsibility[] | undefined,
  homeAorId: string | null,
  descendantIds: string[] | undefined,
  options: Omit<
    Parameters<typeof performZoomToAor>[3],
    'filterIds' | 'ensureVisible'
  >
) {
  if (!aors || !homeAorId) return;

  const homeAor = findAorByHomeId(aors, homeAorId);
  if (!homeAor) return;

  await performZoomToAor(homeAor.id, mapView, aors, {
    ...options,
    filterIds: descendantIds,
    ensureVisible: true,
  });
}

export function useAutoZoomToHomeAor() {
  const {
    mapView,
    aorFilterIds,
    isAorVisible,
    setAorFilterIds,
    setIsAorVisible,
  } = useUscgMapContext();
  const userGuid = useAppSelector(selectUserGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);
  const { data: aors } = useAors();
  const isZoomingRef = useRef(false);

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);
  const homeAorId = currentUserRoster?.home_aor ?? null;
  const resolvedHomeAorId = findAorByHomeId(aors, homeAorId)?.id ?? null;
  const { data: descendants } = useAorDescendants(resolvedHomeAorId);

  const zoomToAorById = useCallback(
    (aorId: string, options: ZoomToAorByIdOptions = {}) => {
      return performZoomToAor(aorId, mapView, aors, {
        ...options,
        currentAorFilterIds: aorFilterIds,
        isAorVisible,
        onFilterChange: setAorFilterIds,
        onVisibilityChange: setIsAorVisible,
        isZoomingRef,
      });
    },
    [
      mapView,
      aors,
      aorFilterIds,
      isAorVisible,
      setAorFilterIds,
      setIsAorVisible,
    ]
  );

  const zoomToHomeAor = useCallback(async () => {
    const descendantIds = descendants?.map((d) => d.id) ?? [];
    return performZoomToHomeAor(mapView, aors, homeAorId, descendantIds, {
      currentAorFilterIds: aorFilterIds,
      isAorVisible,
      onFilterChange: setAorFilterIds,
      onVisibilityChange: setIsAorVisible,
    });
  }, [
    mapView,
    aors,
    homeAorId,
    descendants,
    aorFilterIds,
    isAorVisible,
    setAorFilterIds,
    setIsAorVisible,
  ]);

  return { zoomToHomeAor, zoomToAorById };
}
